import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/view/components/ui/form'
import { Button } from '@/view/components/ui/button'
import {
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/view/components/ui/dialog'
import { PinStep } from './PinStep'
import { ConfirmPinStep } from './ConfirmPinStep'
import { PasswordStep } from './PasswordStep'
import { useToast } from '@/view/components/ui/use-toast'
import { useAuth } from '@/app/hooks/useAuth'

const formSchema = z
  .object({
    pin: z
      .array(z.string())
      .length(4)
      .refine((data) => data.every((digit) => digit !== ''), {
        message: 'PIN é obrigatório',
      }),
    confirmPin: z.array(z.string()).length(4),
    oldPassword: z.string().optional(),
  })
  .refine((data) => data.pin.join('') === data.confirmPin.join(''), {
    message: 'PINs não conferem',
    path: ['confirmPin'],
  })

type FormValues = z.infer<typeof formSchema>

interface PinCreateOrUpdateProps {
  isPending: boolean
  onSubmit: (data: { pin: string[]; oldPassword?: string }) => Promise<void>
}

export function PinCreateOrUpdate({
  isPending,
  onSubmit,
}: PinCreateOrUpdateProps) {
  const [step, setStep] = useState(1)
  const { toast } = useToast()
  const { isAdmin, isSupport } = useAuth()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      pin: Array(4).fill(''),
      confirmPin: Array(4).fill(''),
      oldPassword: '',
    },
    mode: 'onChange',
  })

  const handleNext = async () => {
    if (step === 2) {
      const pin = form.getValues('pin')
      const confirmPin = form.getValues('confirmPin')

      if (pin.join('') !== confirmPin.join('')) {
        toast({
          variant: 'destructive',
          title: 'PINs não conferem',
          description: 'O PIN e a confirmação devem ser iguais.',
        })
        return
      }
    }

    setStep(step + 1)
  }

  const handleBack = () => {
    setStep(step - 1)
  }

  const handleSubmit = async (values: FormValues) => {
    await onSubmit({
      pin: values.pin,
      oldPassword: values.oldPassword,
    })
  }

  const canProceed = () => {
    const values = form.getValues()
    switch (step) {
      case 1:
        return values.pin.every((digit) => digit !== '')
      case 2:
        return values.confirmPin.every((digit) => digit !== '')
      case 3:
        return values.oldPassword !== '' || isAdmin || isSupport
      default:
        return false
    }
  }

  const showStep3 = step === 3 && !isAdmin && !isSupport

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-semibold">
            {step === 1 && 'Digite seu PIN'}
            {step === 2 && 'Confirme seu PIN'}
            {showStep3 && 'Digite sua senha'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {step === 1 && (
            <FormField
              control={form.control}
              name="pin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold">
                    Insira seu PIN
                  </FormLabel>
                  <FormControl>
                    <PinStep
                      pin={field.value}
                      setPin={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {step === 2 && (
            <FormField
              control={form.control}
              name="confirmPin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold">
                    Confirme seu PIN
                  </FormLabel>
                  <FormControl>
                    <ConfirmPinStep
                      pin={field.value}
                      setPin={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {showStep3 && (
            <FormField
              control={form.control}
              name="oldPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-sm font-bold">
                    Insira sua senha
                  </FormLabel>
                  <FormControl>
                    <PasswordStep
                      password={field?.value || ''}
                      setPassword={(value) => field.onChange(value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {step === 3 && (isAdmin || isSupport) && (
            <p>Tem certeza que deseja cadastrar esse PIN?</p>
          )}
        </div>

        <DialogFooter className="mt-4 space-x-2">
          {step > 1 && (
            <Button
              type="button"
              onClick={handleBack}
              variant="outline"
              className="border-white04 border-[1px] bg-transparent text-primary"
            >
              Voltar
            </Button>
          )}

          {step < 3 ? (
            <Button
              type="button"
              onClick={handleNext}
              className="text-white"
              disabled={!canProceed()}
            >
              Próximo
            </Button>
          ) : (
            <Button
              type="submit"
              disabled={isPending || !canProceed()}
              className="text-white"
            >
              Salvar
            </Button>
          )}
        </DialogFooter>
      </form>
    </Form>
  )
}
